import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get, post, del } from "../../lib/request"

// Async thunk for fetching target audiences by client ID
function audienceClientIdFromArg(arg) {
  return arg && typeof arg === "object" && !Array.isArray(arg) ? arg.clientId : arg
}

export const fetchTargetAudiencesByClientId = createAsyncThunk(
  "targetAudience/fetchByClientId",
  async (arg, { rejectWithValue, getState }) => {
    const clientId = audienceClientIdFromArg(arg)
    try {
      const state = getState()
      const token = state.auth?.token

      if (!token) {
        throw new Error("Authentication required. Please login first.")
      }

      if (!clientId) {
        throw new Error("Client ID is required")
      }

      const data = await get(`client-target-audiences/clients/${clientId}`)
      return data
    } catch (err) {
      console.error("Target audience fetch error:", err)
      return rejectWithValue(err?.message || "Failed to fetch target audiences")
    }
  },
  {
    condition: (arg, { getState }) => {
      const force = arg && typeof arg === "object" && !Array.isArray(arg) ? arg.force : false
      const id = audienceClientIdFromArg(arg)
      if (force) return true
      if (id == null || id === "") return false
      const slice = getState().targetAudience ?? {}
      const same = String(slice.clientId ?? "") === String(id)
      if (slice.status === "loading" && same) return false
      if (slice.status === "succeeded" && same) return false
      return true
    },
  },
)

// Async thunk for adding a new target audience tag
export const addTargetAudience = createAsyncThunk(
  "targetAudience/add",
  async ({ clientId, audience, custom = true }, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const token = state.auth?.token

      if (!token) {
        throw new Error("Authentication required. Please login first.")
      }

      const params = new URLSearchParams({
        audience: audience.trim(),
        custom: String(Boolean(custom)),
      })

      const data = await post(
        `client-target-audiences/clients/${clientId}?${params.toString()}`,
      )
      return data
    } catch (err) {
      console.error("Add target audience error:", err)
      return rejectWithValue(err?.message || "Failed to add target audience")
    }
  },
)

// Async thunk for deleting a target audience tag
export const deleteTargetAudience = createAsyncThunk(
  "targetAudience/delete",
  async (id, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const token = state.auth?.token

      if (!token) {
        throw new Error("Authentication required. Please login first.")
      }

      const response = await del(`client-target-audiences/${id}`)
      return id
    } catch (err) {
      console.error("Delete target audience error:", err)
      if (err.message.includes("500") || err.message.includes("Internal Server Error")) {
        return rejectWithValue("Server error occurred. Please try again later.")
      }
      return rejectWithValue(err?.message || "Failed to delete target audience")
    }
  },
)

const initialState = {
  targetAudiences: [],
  suggestedAudiences: [],
  clientId: null,
  status: "idle",
  error: null,
  addStatus: "idle",
  addError: null,
  deleteStatus: "idle",
  deleteError: null,
}

const targetAudienceSlice = createSlice({
  name: "targetAudience",
  initialState,
  reducers: {
    clearTargetAudiences: (state) => {
      state.targetAudiences = []
      state.suggestedAudiences = []
      state.clientId = null
      state.status = "idle"
      state.error = null
    },
    clearTargetAudienceError: (state) => {
      state.error = null
      state.addError = null
      state.deleteError = null
    },
    clearAddStatus: (state) => {
      state.addStatus = "idle"
      state.addError = null
    },
    clearDeleteStatus: (state) => {
      state.deleteStatus = "idle"
      state.deleteError = null
    },
    resetTargetAudienceOnLogout: (state) => {
      state.targetAudiences = []
      state.suggestedAudiences = []
      state.clientId = null
      state.status = "idle"
      state.error = null
      state.addStatus = "idle"
      state.addError = null
      state.deleteStatus = "idle"
      state.deleteError = null
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch target audiences
      .addCase(fetchTargetAudiencesByClientId.pending, (state, action) => {
        state.status = "loading"
        state.error = null
        const nextId = audienceClientIdFromArg(action.meta.arg)
        // Same as social accounts: don't wipe on refetch for the current property.
        if (String(state.clientId ?? "") !== String(nextId ?? "")) {
          state.targetAudiences = []
        }
        if (nextId != null) state.clientId = nextId
      })
      .addCase(fetchTargetAudiencesByClientId.fulfilled, (state, action) => {
        state.status = "succeeded"
        const p = action.payload
        if (p && typeof p === "object" && !Array.isArray(p)) {
          state.targetAudiences = Array.isArray(p.targetAudiences) ? p.targetAudiences : []
          if (Array.isArray(p.suggestedAudiences)) state.suggestedAudiences = p.suggestedAudiences
        } else {
          state.targetAudiences = Array.isArray(p) ? p : (Array.isArray(p?.content) ? p.content : Array.isArray(p?.data) ? p.data : [])
          state.suggestedAudiences = []
        }
        state.clientId = audienceClientIdFromArg(action.meta?.arg) ?? null
      })
      .addCase(fetchTargetAudiencesByClientId.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload || "Failed to fetch target audiences"
      })
      // Add target audience
      .addCase(addTargetAudience.pending, (state) => {
        state.addStatus = "loading"
        state.addError = null
      })
      .addCase(addTargetAudience.fulfilled, (state, action) => {
        state.addStatus = "succeeded"
        const p = action.payload
        if (p && typeof p === "object" && Array.isArray(p.targetAudiences)) {
          state.targetAudiences = p.targetAudiences
          if (Array.isArray(p.suggestedAudiences)) state.suggestedAudiences = p.suggestedAudiences
        } else if (p && typeof p === "object" && p.audience != null) {
          state.targetAudiences.push(p)
        }
      })
      .addCase(addTargetAudience.rejected, (state, action) => {
        state.addStatus = "failed"
        state.addError = action.payload || "Failed to add target audience"
      })
      // Delete target audience
      .addCase(deleteTargetAudience.pending, (state) => {
        state.deleteStatus = "loading"
        state.deleteError = null
      })
      .addCase(deleteTargetAudience.fulfilled, (state, action) => {
        state.deleteStatus = "succeeded"
        state.targetAudiences = state.targetAudiences.filter((audience) => audience.id !== action.payload)
      })
      .addCase(deleteTargetAudience.rejected, (state, action) => {
        state.deleteStatus = "failed"
        state.deleteError = action.payload || "Failed to delete target audience"
      })
  },
})

export const {
  clearTargetAudiences,
  clearTargetAudienceError,
  clearAddStatus,
  clearDeleteStatus,
  resetTargetAudienceOnLogout,
} = targetAudienceSlice.actions

export default targetAudienceSlice.reducer
